import { useParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { practiceAreas } from '../components/PracticeAreasSection';
import { ArrowLeft, Phone, Scale, FileText, Home, Building2, Users, Gavel } from 'lucide-react';

const iconMap = {
  Scale, FileText, Home, Building2, Users, Gavel
};

// Practice area detailed content
const practiceAreaContent = {
  'civil-litigation-patna': {
    title: 'Civil Litigation in Patna',
    metaTitle: 'Civil Litigation Lawyer in Patna | Civil Court Advocate Bihar',
    metaDescription: 'Experienced civil litigation lawyer in Patna handling civil suits, injunctions, declaratory suits before Patna High Court and District Courts.',
    heroText: 'Comprehensive civil litigation services before courts in Patna and throughout Bihar',
    content: `
      <h2>Overview of Civil Litigation Services</h2>
      <p>Civil litigation encompasses a wide range of legal disputes between individuals, businesses, and other entities that do not involve criminal charges. At the Chambers of Adv. Ajeet Narayan Sinha, we provide experienced representation in civil matters before the Patna High Court and various District Courts across Bihar.</p>
      
      <p>With over four decades of practice, Advocate Sinha has handled numerous civil cases involving property disputes, contractual matters, declaratory suits, injunctions, and other civil remedies. Our approach focuses on thorough case preparation, procedural compliance, and effective advocacy.</p>

      <h2>Types of Civil Cases Handled</h2>
      <ul>
        <li><strong>Declaratory Suits:</strong> Seeking court declarations regarding legal rights, status, or other legal character</li>
        <li><strong>Injunction Applications:</strong> Temporary and permanent injunctions to prevent harm or preserve status quo</li>
        <li><strong>Specific Performance:</strong> Enforcement of contractual obligations</li>
        <li><strong>Recovery Suits:</strong> Recovery of money, property, or possession</li>
        <li><strong>Partition Suits:</strong> Division of joint family property or co-owned property</li>
        <li><strong>Title Suits:</strong> Determination of ownership and title to property</li>
      </ul>

      <h2>Civil Court Procedure in Bihar</h2>
      <p>Civil litigation in Bihar follows the Code of Civil Procedure, 1908. The process typically involves:</p>
      <ol>
        <li>Filing of plaint with appropriate court fees</li>
        <li>Scrutiny and registration of the case</li>
        <li>Service of summons to defendants</li>
        <li>Filing of written statement by defendants</li>
        <li>Framing of issues</li>
        <li>Recording of evidence through affidavits and cross-examination</li>
        <li>Arguments and judgment</li>
      </ol>

      <h2>Why Choose Our Chambers</h2>
      <p>Our chambers brings decades of experience in navigating the civil court system in Bihar. We focus on:</p>
      <ul>
        <li>Thorough case analysis and strategy development</li>
        <li>Proper documentation and procedural compliance</li>
        <li>Regular updates to clients on case progress</li>
        <li>Ethical practice in accordance with Bar Council guidelines</li>
      </ul>
    `,
    faqs: [
      {
        q: 'What is the limitation period for filing a civil suit?',
        a: 'Limitation periods vary by type of case under the Limitation Act, 1963. For example, suits for possession have a 12-year limitation, while contract disputes typically have a 3-year limitation. It is important to consult early to preserve your rights.'
      },
      {
        q: 'What documents are required for filing a civil suit?',
        a: 'Documents vary by case type but typically include identity proof, property documents (if applicable), relevant agreements or contracts, correspondence, and any evidence supporting your claim.'
      },
      {
        q: 'How long does a civil case take in Patna courts?',
        a: 'The duration depends on case complexity, evidence requirements, and court workload. Simple matters may conclude within 1-2 years, while complex cases involving multiple issues may take longer.'
      }
    ]
  },
  'property-land-disputes-bihar': {
    title: 'Property & Land Disputes in Bihar',
    metaTitle: 'Property Lawyer in Patna | Land Dispute Advocate Bihar',
    metaDescription: 'Property dispute lawyer in Patna handling land disputes, ownership claims, encroachment matters, and property litigation in Bihar courts.',
    heroText: 'Resolution of property ownership, boundary, and land-related conflicts in Bihar',
    content: `
      <h2>Property Litigation Services</h2>
      <p>Property disputes are among the most common civil matters in Bihar, arising from ownership conflicts, boundary disputes, succession issues, and documentation problems. Our chambers provides experienced representation in property matters before the Patna High Court and District Courts.</p>

      <h2>Common Property Disputes We Handle</h2>
      <ul>
        <li><strong>Ownership Disputes:</strong> Conflicting claims to property ownership</li>
        <li><strong>Boundary Disputes:</strong> Disagreements over property boundaries and demarcation</li>
        <li><strong>Encroachment Matters:</strong> Unauthorized occupation or construction on property</li>
        <li><strong>Succession Disputes:</strong> Property inheritance and succession certificate matters</li>
        <li><strong>Mutation Issues:</strong> Problems with revenue records and mutation proceedings</li>
        <li><strong>Joint Property Division:</strong> Partition of co-owned or joint family property</li>
      </ul>

      <h2>Property Documentation in Bihar</h2>
      <p>Property matters in Bihar involve various types of documentation including:</p>
      <ul>
        <li>Registered Sale Deeds and other conveyance documents</li>
        <li>Khatiyan (Record of Rights) and Khata documents</li>
        <li>Mutation records and tax receipts</li>
        <li>Survey and settlement records</li>
        <li>Succession certificates and legal heir certificates</li>
      </ul>

      <h2>Legal Process for Property Disputes</h2>
      <p>Resolving property disputes typically involves:</p>
      <ol>
        <li>Document verification and title search</li>
        <li>Assessment of claim strength and strategy development</li>
        <li>Filing of appropriate suit with relevant documents</li>
        <li>Interim applications for protection if urgent</li>
        <li>Evidence presentation and arguments</li>
        <li>Execution of decree if successful</li>
      </ol>
    `,
    faqs: [
      {
        q: 'How do I verify property ownership in Bihar?',
        a: 'Property ownership can be verified through registered documents at the Sub-Registrar office, revenue records (Khatiyan/Khata) at Circle Office, and encumbrance certificates. A legal professional can assist with comprehensive title verification.'
      },
      {
        q: 'What is mutation and why is it important?',
        a: 'Mutation is the process of updating revenue records to reflect transfer of property ownership. While mutation does not confer title, it is important for revenue purposes and serves as evidence of possession and ownership claims.'
      },
      {
        q: 'Can I file a case for encroachment on my property?',
        a: 'Yes, if someone has encroached upon your property, you can file a suit for possession along with mandatory injunction for removal of construction. Temporary injunction may be sought to prevent further construction during pendency of the suit.'
      }
    ]
  },
  'contract-commercial-disputes': {
    title: 'Contract & Commercial Disputes',
    metaTitle: 'Contract Dispute Lawyer Patna | Commercial Litigation Bihar',
    metaDescription: 'Contract dispute lawyer in Patna handling breach of contract, commercial litigation, recovery suits, and business disputes in Bihar.',
    heroText: 'Legal assistance in contract enforcement and commercial litigation',
    content: `
      <h2>Contract and Commercial Dispute Services</h2>
      <p>Commercial relationships are governed by contracts and legal obligations. When disputes arise from breach of contract, non-payment, or other commercial issues, effective legal representation is essential. Our chambers handles various contract and commercial matters before Bihar courts.</p>

      <h2>Types of Contract Disputes</h2>
      <ul>
        <li><strong>Breach of Contract:</strong> Non-performance or violation of contractual terms</li>
        <li><strong>Recovery Suits:</strong> Recovery of money owed under contracts</li>
        <li><strong>Specific Performance:</strong> Enforcement of contractual obligations</li>
        <li><strong>Partnership Disputes:</strong> Conflicts between business partners</li>
        <li><strong>Service Agreements:</strong> Disputes arising from service contracts</li>
        <li><strong>Sale Disputes:</strong> Issues related to sale agreements and transactions</li>
      </ul>

      <h2>Key Legal Provisions</h2>
      <p>Contract disputes in India are primarily governed by:</p>
      <ul>
        <li>Indian Contract Act, 1872</li>
        <li>Specific Relief Act, 1963</li>
        <li>Sale of Goods Act, 1930</li>
        <li>Indian Partnership Act, 1932 (for partnership matters)</li>
      </ul>

      <h2>Remedies Available</h2>
      <p>Depending on the nature of the dispute, available remedies include:</p>
      <ul>
        <li>Damages for breach of contract</li>
        <li>Specific performance of contract</li>
        <li>Injunction to prevent breach</li>
        <li>Rescission of contract</li>
        <li>Restitution of benefits received</li>
      </ul>
    `,
    faqs: [
      {
        q: 'What is the limitation period for filing a contract dispute case?',
        a: 'Under the Limitation Act, suits for breach of contract must generally be filed within 3 years from the date of breach. For specific performance, the period is also 3 years from the date fixed for performance.'
      },
      {
        q: 'Can I recover money without a written contract?',
        a: 'Yes, oral contracts are valid under Indian law. However, proving the terms of an oral agreement requires sufficient evidence such as witnesses, correspondence, payment records, or conduct of parties.'
      },
      {
        q: 'What is specific performance of contract?',
        a: 'Specific performance is a remedy where the court orders the party to perform their contractual obligations. It is typically granted when monetary damages would not adequately compensate the aggrieved party.'
      }
    ]
  },
  'rent-eviction-matters': {
    title: 'Rent & Eviction Matters',
    metaTitle: 'Rent Lawyer Patna | Eviction Advocate Bihar | Tenant Dispute',
    metaDescription: 'Rent and eviction lawyer in Patna handling landlord-tenant disputes, eviction suits, and rent control matters in Bihar courts.',
    heroText: 'Legal services for landlord-tenant disputes and eviction proceedings',
    content: `
      <h2>Rent and Eviction Legal Services</h2>
      <p>Landlord-tenant relationships are governed by specific tenancy laws and contractual agreements. Our chambers provides representation in rent-related matters including eviction suits, rent disputes, and tenancy issues before appropriate forums in Bihar.</p>

      <h2>Types of Rent Matters Handled</h2>
      <ul>
        <li><strong>Eviction Suits:</strong> Legal proceedings for recovery of possession</li>
        <li><strong>Rent Recovery:</strong> Suits for recovery of unpaid rent</li>
        <li><strong>Tenancy Disputes:</strong> Conflicts regarding tenancy terms and conditions</li>
        <li><strong>Unlawful Subletting:</strong> Matters involving unauthorized subletting</li>
        <li><strong>Rent Revision:</strong> Disputes regarding standard rent and revisions</li>
        <li><strong>Lease Agreements:</strong> Drafting and enforcement of lease terms</li>
      </ul>

      <h2>Grounds for Eviction in Bihar</h2>
      <p>Under applicable tenancy laws, common grounds for eviction include:</p>
      <ul>
        <li>Non-payment of rent</li>
        <li>Subletting without permission</li>
        <li>Causing damage to the property</li>
        <li>Using premises for unauthorized purposes</li>
        <li>Bona fide requirement of landlord</li>
        <li>Denial of landlord's title</li>
      </ul>

      <h2>Legal Framework</h2>
      <p>Rent and tenancy matters in Bihar are governed by:</p>
      <ul>
        <li>Bihar Buildings (Lease, Rent and Eviction) Control Act</li>
        <li>Transfer of Property Act (for lease provisions)</li>
        <li>Specific lease agreement terms</li>
      </ul>
    `,
    faqs: [
      {
        q: 'What is the process for evicting a tenant in Bihar?',
        a: 'Eviction requires filing a suit before the appropriate court with grounds specified under tenancy laws. The tenant has a right to contest the suit. If the court finds merit in the landlord\'s case, an eviction decree is passed which can be executed if the tenant does not vacate.'
      },
      {
        q: 'Can a landlord increase rent during tenancy?',
        a: 'Rent revision depends on the terms of the lease agreement and applicable rent control laws. In rent-controlled premises, increases may be limited by statute. In other cases, the lease agreement governs rent revision.'
      },
      {
        q: 'What if a tenant refuses to vacate after lease expiry?',
        a: 'If a tenant continues in possession after lease expiry without renewal, the landlord may file an eviction suit. The tenant may be treated as a tenant at sufferance or month-to-month tenant depending on circumstances.'
      }
    ]
  },
  'title-partition-suits': {
    title: 'Title & Partition Suits',
    metaTitle: 'Partition Suit Lawyer Patna | Title Dispute Advocate Bihar',
    metaDescription: 'Partition suit and title dispute lawyer in Patna handling property division, succession matters, and joint family partition in Bihar.',
    heroText: 'Legal proceedings for property title verification and partition',
    content: `
      <h2>Title and Partition Legal Services</h2>
      <p>Title disputes and partition matters involve complex questions of ownership, co-ownership rights, and division of property. Our chambers handles title suits and partition matters before the Patna High Court and District Courts in Bihar.</p>

      <h2>Types of Cases We Handle</h2>
      <ul>
        <li><strong>Title Suits:</strong> Declaration of ownership and title to property</li>
        <li><strong>Partition Suits:</strong> Division of joint family or co-owned property</li>
        <li><strong>Succession Matters:</strong> Property rights arising from inheritance</li>
        <li><strong>Joint Family Property:</strong> Rights in Hindu Undivided Family property</li>
        <li><strong>Coparcenary Rights:</strong> Claims in ancestral property</li>
        <li><strong>Adverse Possession:</strong> Claims based on long possession</li>
      </ul>

      <h2>Partition Suit Process</h2>
      <p>A partition suit typically involves:</p>
      <ol>
        <li>Filing of suit with details of property and sharers</li>
        <li>Determination of shares of each party</li>
        <li>Appointment of Commissioner for property division</li>
        <li>Commissioner's report on property division</li>
        <li>Objections to Commissioner's report, if any</li>
        <li>Final decree allotting shares to parties</li>
      </ol>

      <h2>Legal Framework</h2>
      <p>Title and partition matters are governed by:</p>
      <ul>
        <li>Hindu Succession Act (for Hindu families)</li>
        <li>Indian Succession Act (for other communities)</li>
        <li>Partition Act, 1893</li>
        <li>Transfer of Property Act</li>
        <li>Personal laws applicable to parties</li>
      </ul>
    `,
    faqs: [
      {
        q: 'Who can file a partition suit?',
        a: 'Any co-owner or coparcener who has a share in the property can file a partition suit. This includes members of a Hindu Undivided Family, co-owners who purchased property together, or legal heirs who inherited property jointly.'
      },
      {
        q: 'What is the difference between preliminary and final decree in partition?',
        a: 'The preliminary decree determines the shares of each party in the property. The final decree actually divides the property according to those shares and is passed after the Commissioner\'s report on division.'
      },
      {
        q: 'Can ancestral property be partitioned?',
        a: 'Yes, coparceners in Hindu law have a right to seek partition of ancestral property. After the 2005 amendment to the Hindu Succession Act, daughters also have equal coparcenary rights in ancestral property.'
      }
    ]
  },
  'civil-appeals-patna-high-court': {
    title: 'Civil Appeals - Patna High Court',
    metaTitle: 'Civil Appeal Lawyer Patna High Court | Appellate Practice Bihar',
    metaDescription: 'Civil appeals lawyer at Patna High Court handling first appeals, second appeals, revision petitions, and writ petitions in Bihar.',
    heroText: 'Appellate practice before the Patna High Court',
    content: `
      <h2>Civil Appellate Practice</h2>
      <p>Appeals are an essential part of the civil justice system, providing parties the opportunity to have higher courts review decisions of lower courts. Our chambers handles civil appeals before the Patna High Court, including first appeals, second appeals, revision petitions, and writ petitions.</p>

      <h2>Types of Appeals We Handle</h2>
      <ul>
        <li><strong>First Appeals:</strong> Appeals from decrees of District/Civil Courts on questions of fact and law</li>
        <li><strong>Second Appeals:</strong> Appeals involving substantial questions of law</li>
        <li><strong>Revision Petitions:</strong> Supervisory jurisdiction of High Court over lower courts</li>
        <li><strong>Writ Petitions:</strong> Constitutional remedies under Article 226</li>
        <li><strong>Letters Patent Appeals:</strong> Appeals from Single Judge to Division Bench</li>
        <li><strong>Miscellaneous Appeals:</strong> Appeals from interlocutory orders</li>
      </ul>

      <h2>Appellate Procedure</h2>
      <p>The appellate process before the Patna High Court involves:</p>
      <ol>
        <li>Filing of memorandum of appeal with grounds</li>
        <li>Obtaining certified copies of impugned order and relevant documents</li>
        <li>Admission hearing for appeals</li>
        <li>Notice to respondents</li>
        <li>Filing of counter-affidavit/reply</li>
        <li>Final hearing and judgment</li>
      </ol>

      <h2>Grounds for Appeals</h2>
      <p>Appeals may be filed on various grounds including:</p>
      <ul>
        <li>Error in appreciation of evidence</li>
        <li>Misapplication of law</li>
        <li>Procedural irregularities</li>
        <li>Jurisdictional errors</li>
        <li>Perversity of findings</li>
      </ul>

      <h2>Limitation for Appeals</h2>
      <p>Time limits for filing appeals are prescribed under the Limitation Act:</p>
      <ul>
        <li>First Appeal: 90 days from date of decree</li>
        <li>Second Appeal: 90 days from date of appellate decree</li>
        <li>Revision: 90 days from date of order</li>
      </ul>
    `,
    faqs: [
      {
        q: 'What is the difference between first appeal and second appeal?',
        a: 'A first appeal lies from the original decree of a trial court and can challenge findings of fact and law. A second appeal lies from the appellate decree and can only be filed if there is a substantial question of law involved.'
      },
      {
        q: 'Can I file an appeal after the limitation period?',
        a: 'Applications for condonation of delay can be filed explaining sufficient cause for the delay. The court has discretion to condone delay if satisfied that the delay was not willful or negligent.'
      },
      {
        q: 'What is a writ petition under Article 226?',
        a: 'Article 226 empowers High Courts to issue writs including habeas corpus, mandamus, prohibition, certiorari, and quo warranto for enforcement of fundamental rights and for any other purpose. It provides a constitutional remedy against state action.'
      }
    ]
  }
};

export default function PracticeAreaPage() {
  const { slug } = useParams();
  const content = practiceAreaContent[slug];
  const practiceArea = practiceAreas.find(pa => pa.slug === `/practice-areas/${slug}`);

  if (!content || !practiceArea) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen bg-[#F8F6F2]">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-[#0B1C2D] mb-4">Page Not Found</h1>
            <p className="text-[#4A5568] mb-8">The practice area you're looking for doesn't exist.</p>
            <Link to="/">
              <Button className="bg-[#0B1C2D] hover:bg-[#1A2E44] text-white">
                Return to Home
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const IconComponent = iconMap[practiceArea.icon.name] || Scale;

  return (
    <>
      <Navbar />
      <main className="pt-20" data-testid={`practice-area-page-${slug}`}>
        {/* Hero Section */}
        <section className="bg-[#0B1C2D] py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to="/#practice-areas" 
              className="inline-flex items-center text-[#C6A34A] hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              All Practice Areas
            </Link>
            
            <div className="flex items-start space-x-6">
              <div className="w-16 h-16 bg-[#C6A34A] flex items-center justify-center flex-shrink-0">
                <IconComponent className="w-8 h-8 text-[#0B1C2D]" />
              </div>
              <div>
                <h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {content.title}
                </h1>
                <div className="w-20 h-1 bg-[#C6A34A] mb-4" />
                <p className="text-gray-300 text-lg max-w-2xl">
                  {content.heroText}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 lg:py-24 bg-[#F8F6F2]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white p-8 lg:p-12 shadow-sm border border-gray-100">
              <article 
                className="prose prose-gray max-w-none 
                  prose-headings:font-semibold prose-headings:text-[#0B1C2D]
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-p:text-[#4A5568] prose-p:leading-relaxed
                  prose-ul:text-[#4A5568] prose-ol:text-[#4A5568]
                  prose-li:my-1
                  prose-strong:text-[#0B1C2D]"
                style={{ fontFamily: "'Lato', sans-serif" }}
                dangerouslySetInnerHTML={{ __html: content.content }}
              />
            </div>

            {/* FAQ Section */}
            {content.faqs && content.faqs.length > 0 && (
              <div className="mt-12 bg-white p-8 lg:p-12 shadow-sm border border-gray-100">
                <h2 
                  className="text-2xl font-semibold text-[#0B1C2D] mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Frequently Asked Questions
                </h2>
                <div className="w-16 h-1 bg-[#C6A34A] mb-8" />
                
                <div className="space-y-6">
                  {content.faqs.map((faq, index) => (
                    <div key={index} className="border-b border-gray-100 pb-6 last:border-0">
                      <h3 
                        className="text-lg font-medium text-[#0B1C2D] mb-3"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {faq.q}
                      </h3>
                      <p className="text-[#4A5568] leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-12 bg-[#0B1C2D] p-8 lg:p-12 text-center">
              <h3 
                className="text-2xl font-semibold text-white mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Have a Legal Query?
              </h3>
              <p className="text-gray-300 mb-6">
                Submit your inquiry and we will respond at the earliest.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/#inquiry-form">
                  <Button className="bg-[#C6A34A] hover:bg-[#D4B055] text-white px-8 py-6">
                    Submit Legal Query
                  </Button>
                </Link>
                <a href="tel:+919431078694">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6">
                    <Phone className="w-4 h-4 mr-2" />
                    +91 9431078694
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Page Schema */}
      <script type="application/ld+json" className="schema-markup" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": content.metaTitle,
          "description": content.metaDescription,
          "mainEntity": {
            "@type": "LegalService",
            "name": content.title,
            "provider": {
              "@type": "Attorney",
              "name": "Adv. Ajeet Narayan Sinha"
            }
          }
        })
      }} />
    </>
  );
}
